/**
 * 프로덕션에서 `/api/*` 상대 요청이 현재 호스트(Pages)로 가면 SPA의 index.html(HTML)이 와서
 * JSON 파싱 에러·hasMore undefined 등이 발생한다.
 * REACT_APP_API_URL 이 있으면 axios 기본 baseURL 과 fetch 를 백엔드 오리진으로 맞춘다.
 *
 * Pages(astrantis.net) → Railway(다른 사이트) 요청에는 HttpOnly 쿠키가 SameSite=Strict 면
 * 브라우저가 쿠키를 붙이지 않는다. localStorage 의 auth_token 을 Authorization: Bearer 로 붙인다.
 * 개발에서는 setupProxy가 /api 를 로컬 백엔드로 넘기므로 여기서는 아무 것도 하지 않는다.
 */
import axios from 'axios';

const isDev = process.env.NODE_ENV === 'development';
const base = String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function attachBearerForApiOrigin(absoluteUrl, init) {
  if (!base || !absoluteUrl.startsWith(base)) return init;
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return init;
    const next = init ? { ...init } : {};
    const h = new Headers(next.headers);
    if (!h.has('Authorization')) {
      h.set('Authorization', `Bearer ${token}`);
    }
    next.headers = h;
    return next;
  } catch {
    return init;
  }
}

if (!isDev) {
  if (base) {
    axios.defaults.baseURL = base;
    // axios 1.x 는 config.headers 가 AxiosHeaders 인스턴스인 경우가 많음.
    // `config.headers = config.headers || {}` 로 바꾸면 병합·직렬화가 깨져 요청이 실패하거나
    // 응답이 비정상일 수 있음(프로덕션에서만 증상). Authorization 만 안전하게 붙인다.
    axios.interceptors.request.use((config) => {
      // 홈 공개 읽기 모델은 사용자와 무관한 동일 응답이어야 CDN/브라우저 캐시를 공유할 수 있다.
      if (config.skipAuth === true) return config;
      const token =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem('auth_token')
          : null;
      if (!token) return config;
      const h = config.headers;
      if (!h) return config;
      if (typeof h.set === 'function') {
        if (!h.get('Authorization')) {
          h.set('Authorization', `Bearer ${token}`);
        }
      } else if (!h.Authorization) {
        h.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    const origFetch = window.fetch.bind(window);
    window.fetch = (input, init) => {
      if (typeof input === 'string') {
        const url = input.startsWith('/api') ? `${base}${input}` : input;
        if (url.startsWith(base)) {
          return origFetch(url, attachBearerForApiOrigin(url, init));
        }
        return origFetch(input, init);
      }
      if (typeof Request !== 'undefined' && input instanceof Request) {
        try {
          const parsed = new URL(input.url, window.location.origin);
          if (
            parsed.origin === window.location.origin &&
            parsed.pathname.startsWith('/api')
          ) {
            const newUrl = `${base}${parsed.pathname}${parsed.search}${parsed.hash}`;
            return origFetch(
              new Request(newUrl, input),
              attachBearerForApiOrigin(newUrl, init),
            );
          }
        } catch {
          /* ignore */
        }
      }
      return origFetch(input, init);
    };
  } else {
    // eslint-disable-next-line no-console
    console.warn(
      '[Astrantis] REACT_APP_API_URL is missing. Set it in Cloudflare Pages → Settings → Environment variables to your Railway URL (e.g. https://xxx.up.railway.app), then rebuild.',
    );
  }
}
